import React, { useState, useEffect, useRef } from 'react';
import { getProject, getScreens, uploadScreen, deleteScreen, reorderScreens, updateVictorySettings, BASE_URL } from '../api';
import { useTheme } from '../ThemeContext';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';

const DEFAULT_VICTORY_TITLE   = "Well done!";
const DEFAULT_VICTORY_MESSAGE = "You've completed the simulation successfully!";

export default function ScreenManager() {
  const { colors, common, mode } = useTheme();
  const toast = useToast();
  const projectId = window.location.pathname.split('/')[2];
  const [project, setProject]   = useState(null);
  const [screens, setScreens]   = useState([]);
  const [uploading, setUploading]           = useState(false);
  const [exporting, setExporting]           = useState(false);
  const [exportingScorm, setExportingScorm] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Victory screen settings
  const [victoryTitle,   setVictoryTitle]   = useState(DEFAULT_VICTORY_TITLE);
  const [victoryMessage, setVictoryMessage] = useState(DEFAULT_VICTORY_MESSAGE);
  const [victoryEffect,  setVictoryEffect]  = useState('confetti');
  const [redirectUrl,    setRedirectUrl]    = useState('');
  const [victorySaving,  setVictorySaving]  = useState(false);

  // Drag state — ref tracks the source index, state tracks both for rendering
  const dragIndex      = useRef(null);
  const [draggingIndex, setDraggingIndex] = useState(null);
  const [dragOver, setDragOver] = useState(null); // index being hovered

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const { data: proj } = await getProject(projectId);
      setProject(proj);
      setVictoryTitle(proj.victory_title     || DEFAULT_VICTORY_TITLE);
      setVictoryMessage(proj.victory_message || DEFAULT_VICTORY_MESSAGE);
      setVictoryEffect(proj.victory_effect   || 'confetti');
      setRedirectUrl(proj.redirect_url || '');
      const { data: scr } = await getScreens(projectId);
      setScreens(scr);
    } catch (err) { console.error('Could not load project'); }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('image', file);
        await uploadScreen(projectId, formData);
      }
      fetchData();
    } catch (err) { console.error('Upload failed'); }
    setUploading(false);
  };

  const handleDelete = (id) => {
    setConfirmDeleteId(id);
  };

  const doDelete = async () => {
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    try {
      await deleteScreen(id);
      fetchData();
    } catch (err) {
      toast('Could not delete screen. Please try again.', 'error');
    }
  };

  const handlePaste = async () => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      let foundImage = false;
      for (const item of clipboardItems) {
        const imageType = item.types.find(type => type.startsWith('image/'));
        if (imageType) {
          foundImage = true;
          setUploading(true);
          const blob = await item.getType(imageType);
          const file = new File([blob], `paste-${Date.now()}.png`, { type: imageType });
          const formData = new FormData();
          formData.append('image', file);
          await uploadScreen(projectId, formData);
          fetchData();
          setUploading(false);
        }
      }
      if (!foundImage) toast('No image found in clipboard. Please copy an image first.', 'error');
    } catch (err) {
      toast('Could not access clipboard. Please make sure you have copied an image.', 'error');
    }
  };

  const handleExport = async () => {
    if (screens.length === 0) { toast('Add at least one screen before exporting.', 'error'); return; }
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BASE_URL}/api/export/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = `${project?.title || 'simulation'}.zip`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast('Export failed. Please try again.', 'error'); }
    setExporting(false);
  };

  const handleScormExport = async () => {
    if (screens.length === 0) { toast('Add at least one screen before exporting.', 'error'); return; }
    setExportingScorm(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BASE_URL}/api/export/${projectId}/scorm`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('SCORM export failed');
      const blob = await response.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = `${project?.title || 'simulation'}_scorm.zip`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast('SCORM export failed. Please try again.', 'error'); }
    setExportingScorm(false);
  };

  // ── Drag-and-drop handlers ────────────────────────────────────────────────

  const handleSaveVictory = async () => {
    setVictorySaving(true);
    try {
      await updateVictorySettings(projectId, {
        victory_title:   victoryTitle,
        victory_message: victoryMessage,
        victory_effect:  victoryEffect,
        redirect_url:    redirectUrl,
      });
      toast('Victory screen settings saved.', 'success');
    } catch {
      toast('Could not save victory settings. Please try again.', 'error');
    }
    setVictorySaving(false);
  };

  const handleDragStart = (index) => {
    dragIndex.current = index;
    setDraggingIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (dragIndex.current === null || dragIndex.current === index) return;
    setDragOver(index);
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    const fromIndex = dragIndex.current;
    dragIndex.current = null;
    setDragOver(null);
    if (fromIndex === null || fromIndex === dropIndex) return;

    const reordered = [...screens];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    setScreens(reordered);
    setDraggingIndex(null);

    try {
      await reorderScreens(projectId, reordered.map((s, i) => ({ id: s.id, order_index: i })));
    } catch (err) {
      console.error('Reorder failed'); fetchData();
    }
  };

  const handleDragEnd = () => {
    dragIndex.current = null;
    setDraggingIndex(null);
    setDragOver(null);
  };

  return (
    <div style={{ ...common.page, minHeight: 'unset', flex: 1 }}>
      <header style={common.header}>
        <div style={common.headerLeft}>
          <a href="/dashboard" aria-label="Back to dashboard" style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <img src={mode === 'dark' ? '/Logo-Dark.png' : '/Logo-Light.png'} alt="Software Simulation Builder logo" style={{ height: 32, width: 'auto' }} />
          </a>
          <a href="/dashboard" style={{ ...common.btn, ...common.btnGhost, textDecoration: 'none' }}
            aria-label="Back to dashboard">← Back</a>
          <h1 style={common.appTitle}>{project?.title || 'Loading...'}</h1>
          <a href={`/projects/${projectId}/preview`}
            style={{ ...common.btn, ...common.btnSuccess, textDecoration: 'none' }}
            aria-label="Preview simulation">▶ Preview</a>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            style={{ ...common.btn, ...common.btnPrimary, opacity: exporting ? 0.7 : 1 }}
            onClick={handleExport} disabled={exporting}>
            {exporting ? '⏳ Exporting...' : '⬇ Export HTML'}
          </button>
          <button
            style={{ ...common.btn, ...common.btnPrimary, opacity: exportingScorm ? 0.7 : 1 }}
            onClick={handleScormExport} disabled={exportingScorm}>
            {exportingScorm ? '⏳ Exporting...' : '⬇ Export SCORM'}
          </button>
        </div>
      </header>

      <main id="main-content" style={common.content}>
        <div style={common.topBar}>
          <div>
            <h2 style={common.sectionHeading}>Screens</h2>
            {screens.length > 1 && (
              <p style={{ color: colors.textMuted, fontSize: 12, marginTop: 4 }}>
                Drag cards to reorder
              </p>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label style={{ ...common.btn, ...common.btnPrimary, cursor: 'pointer' }}
              aria-label={uploading ? 'Uploading screenshots' : 'Upload screenshots'}>
              {uploading ? 'Uploading...' : '+ Upload Screenshots'}
              <input type="file" accept="image/*" multiple onChange={handleUpload} style={{ display: 'none' }} />
            </label>
            <button style={{ ...common.btn, ...common.btnSecondary }} onClick={handlePaste}>
              📋 Paste from Clipboard
            </button>
          </div>
        </div>

        {screens.length === 0 ? (
          <div style={common.empty}>
            <p style={{ marginBottom: 8 }}>No screens yet.</p>
            <p>Click <strong style={{ color: colors.textPrimary }}>+ Upload Screenshots</strong> to add your first screen!</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 24 }}>
            {screens.map((screen, index) => {
              const isOver = dragOver === index;
              return (
                <div
                  key={screen.id}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  style={{
                    ...common.card,
                    cursor: 'grab',
                    transition: 'transform 0.15s, box-shadow 0.15s, opacity 0.15s',
                    opacity: draggingIndex === index ? 0.4 : 1,
                    outline: isOver ? `2px dashed ${colors.indigo}` : 'none',
                    outlineOffset: 2,
                  }}
                >
                  {/* Drag handle strip */}
                  <div style={{
                    background: isOver
                      ? `linear-gradient(90deg, ${colors.indigo}, #7c3aed)`
                      : colors.borderColor,
                    height: 4,
                    transition: 'background 0.15s',
                  }} />

                  <div style={{ position: 'relative' }}>
                    <img
                      src={`${BASE_URL}/uploads/${screen.image_path}`}
                      alt={`Screen ${index + 1}`}
                      style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }}
                      draggable={false}
                    />
                    {/* Number badge */}
                    <div style={{
                      position: 'absolute', top: 8, left: 8,
                      backgroundColor: colors.indigo, color: colors.white,
                      borderRadius: '50%', width: 28, height: 28,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, fontWeight: 700, boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
                    }}>
                      {index + 1}
                    </div>
                    {/* Victory screen badge on last card */}
                    {index === screens.length - 1 && (
                      <div style={{
                        position: 'absolute', bottom: 8, left: 8, right: 8,
                        backgroundColor: 'rgba(245,158,11,0.92)',
                        color: '#fff', fontSize: 11, fontWeight: 700,
                        borderRadius: 6, padding: '3px 8px',
                        display: 'flex', alignItems: 'center', gap: 5,
                        boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
                      }}>
                        🏆 Victory Screen — triggers completion
                      </div>
                    )}
                    {/* Drag handle icon */}
                    <div style={{
                      position: 'absolute', top: 8, right: 8,
                      color: 'rgba(255,255,255,0.7)', fontSize: 16, lineHeight: 1,
                      userSelect: 'none', textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                    }} title="Drag to reorder">⠿</div>
                  </div>

                  <div style={{ display: 'flex', gap: 8, padding: 12 }}>
                    <a href={`/projects/${projectId}/edit/${screen.id}`}
                      style={{ ...common.btn, ...common.btnPrimary, flex: 1, justifyContent: 'center', textDecoration: 'none' }}
                      aria-label={`Edit hotspots for screen ${index + 1}`}>
                      Edit Hotspots
                    </a>
                    <button style={{ ...common.btn, ...common.btnDanger, flex: 1, justifyContent: 'center' }}
                      onClick={() => handleDelete(screen.id)}>
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Victory Screen Settings ── */}
        {screens.length > 0 && (
          <div style={{
            marginTop: 40,
            border: `1px solid ${colors.borderColor}`,
            borderLeft: `4px solid ${colors.amber}`,
            borderRadius: 10,
            backgroundColor: colors.cardBg,
            padding: '20px 24px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <span style={{ fontSize: 20 }}>🏆</span>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: colors.textPrimary }}>
                Victory Screen Settings
              </h2>
            </div>
            <p style={{ margin: '0 0 20px', fontSize: 13, color: colors.textSecondary }}>
              Customise what learners see when they complete the simulation. These settings apply to both the Preview and exported files.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={common.label} htmlFor="victory-title">Completion Title</label>
                <input
                  id="victory-title"
                  style={common.input}
                  type="text"
                  value={victoryTitle}
                  onChange={(e) => setVictoryTitle(e.target.value)}
                  placeholder={DEFAULT_VICTORY_TITLE}
                />
              </div>
              <div>
                <label style={common.label} htmlFor="redirect-url">Redirect URL after completion</label>
                <input
                  id="redirect-url"
                  style={common.input}
                  type="url"
                  value={redirectUrl}
                  onChange={(e) => setRedirectUrl(e.target.value)}
                  placeholder="https://example.com/next-steps (optional)"
                />
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={common.label} htmlFor="victory-message">Completion Message</label>
              <textarea
                id="victory-message"
                style={{ ...common.input, resize: 'vertical', height: 72 }}
                value={victoryMessage}
                onChange={(e) => setVictoryMessage(e.target.value)}
                placeholder={DEFAULT_VICTORY_MESSAGE}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ ...common.label, display: 'block', marginBottom: 10 }}>Completion Effect</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[
                  { value: 'confetti',  label: 'Confetti',  emoji: '🎊' },
                  { value: 'fireworks', label: 'Fireworks', emoji: '🎆' },
                  { value: 'balloons',  label: 'Balloons',  emoji: '🎈' },
                  { value: 'stars',     label: 'Stars',     emoji: '⭐' },
                  { value: 'none',      label: 'None',      emoji: '🚫' },
                ].map(effect => {
                  const isSelected = victoryEffect === effect.value;
                  return (
                    <button
                      key={effect.value}
                      onClick={() => setVictoryEffect(effect.value)}
                      style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        gap: 4, padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                        border: isSelected ? `2px solid ${colors.amber}` : `2px solid ${mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
                        background: isSelected ? (mode === 'dark' ? 'rgba(245,158,11,0.15)' : 'rgba(245,158,11,0.1)') : 'transparent',
                        color: colors.textPrimary, minWidth: 72,
                      }}
                    >
                      <span style={{ fontSize: 24 }}>{effect.emoji}</span>
                      <span style={{ fontSize: 11, fontWeight: isSelected ? 600 : 400 }}>{effect.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              style={{ ...common.btn, ...common.btnPrimary, opacity: victorySaving ? 0.7 : 1 }}
              onClick={handleSaveVictory}
              disabled={victorySaving}
            >
              {victorySaving ? 'Saving…' : 'Save Victory Settings'}
            </button>
          </div>
        )}
      </main>

      {confirmDeleteId !== null && (
        <ConfirmDialog
          message="Delete this screen? Any hotspots on it will also be deleted."
          confirmLabel="Delete Screen"
          onConfirm={doDelete}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}
    </div>
  );
}
